import { formatRupees, type LabPlan, type PlanStatus } from '@quickreport/domain';

export interface PlanNotice {
  /** Changes when the situation changes, so a notice that was read comes back when it matters again. */
  key: string;
  title: string;
  body: string;
  tone: 'info' | 'warn' | 'alert';
}

const day = (iso: string | null) =>
  iso
    ? new Date(iso).toLocaleDateString('en-IN', { day: 'numeric', month: 'short', year: 'numeric' })
    : '';

/** What to tell the lab about its plan, in plain words. Nothing when there is nothing to say. */
export function planNotice(plan: LabPlan | null, status: PlanStatus): PlanNotice | null {
  if (!plan || !status.needsAttention) return null;
  const key = `plan:${status.state}:${plan.expiresAt ?? ''}:${plan.name}`;
  switch (status.state) {
    case 'ending': {
      const n = status.daysLeft ?? 0;
      return {
        key,
        tone: 'info',
        title: n <= 1 ? 'Your plan ends tomorrow' : `Your plan ends in ${n} days`,
        body: `${plan.name} runs until ${day(plan.expiresAt)}. Please choose a plan before then so LabView carries on without a break.`,
      };
    }
    case 'expired': {
      const ago = Math.abs(status.daysLeft ?? 0);
      return {
        key,
        tone: 'warn',
        title: 'Your plan has ended',
        body: `${plan.name} ended on ${day(plan.expiresAt)}${ago > 0 ? ` (${ago} day${ago === 1 ? '' : 's'} ago)` : ''}. LabView still works, but please choose a new plan to stay covered.`,
      };
    }
    case 'low':
      return {
        key,
        tone: 'info',
        title: 'Your wallet is running low',
        body: `${formatRupees(plan.wallet.balancePaise)} is left, enough for about ${status.reportsLeft} more reports. Ask LabView to add credit.`,
      };
    case 'credit':
      return {
        key,
        tone: 'warn',
        title: 'Your wallet is empty: using your credit',
        body: `You can still make about ${status.reportsLeft} more report${status.reportsLeft === 1 ? '' : 's'} before credit is needed. Ask LabView to add credit.`,
      };
    case 'empty':
      return {
        key,
        tone: 'alert',
        title: 'No credit left for new reports',
        body: 'New reports are paused until credit is added. Reports you have already started can still be finished and signed.',
      };
    default:
      return null;
  }
}
